import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Landmark, History, ChevronRight, Wallet as WalletIcon, ArrowLeft } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { getPromoterEarnings } from "../../services/walletService";
import Loader from "../../components/common/Loader";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import EmptyState from "../../components/common/EmptyState";
import Pagination from "../../components/common/Pagination";
import { color, shadow } from "../../styles/theme";

const PAGE_SIZE = 8;

const galleryId = (item) => item?.GalleryId ?? item?.Id ?? item?.galleryId ?? null;

/**
 * Wallet — ported from mobile's my-wallet screen (src/Screens/my-wallet):
 * earnings summary + paginated gallery list. "View Details" actually
 * navigates to Gallery Payment Detail here (mobile registers that screen but
 * never wires a navigation to it — its own card just shows an info alert).
 */
const Wallet = () => {
  const { t } = useTranslation(["wallet", "common"]);
  const { user } = useAuth();
  const navigate = useNavigate();
  // Present when opened via Event Settings' "Wallet" row, so Back can return
  // there instead of leaving the user stranded on this standalone page.
  const fromEventId = useLocation().state?.fromEventId;

  const [summary, setSummary] = useState({ totalEarning: 0, totalReceivable: 0, totalReceived: 0 });
  const [galleries, setGalleries] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = (pageNumber = 1) => {
    (pageNumber === 1 ? setLoading : setLoadingMore)(true);
    getPromoterEarnings({ promoterId: user?.UserId, eventId: "", pageNumber, pageSize: PAGE_SIZE })
      .then((res) => {
        const result = res.data?.Result?.[0] || {};
        setGalleries(result.Galleries || []);
        setSummary({
          totalEarning: result.TotalEarnings ?? 0,
          totalReceivable: result.TotalReceivable ?? 0,
          totalReceived: result.TotalReceived ?? 0,
        });
        setMeta(res.data?.Metadata || null);
        setPage(pageNumber);
      })
      .catch(() => toast.error(t("wallet.loadError")))
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Loader />;

  return (
    <div>
      {fromEventId && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          style={s.backLink}
          onClick={() => navigate(`/events/${fromEventId}`, { state: { initialTab: "Settings" } })}
        >
          <ArrowLeft size={13} strokeWidth={2.25} /> {t("common:actions.backToEventSettings")}
        </button>
      )}
      <div style={s.topBar}>
        <h2 style={s.title}>{t("wallet.title")}</h2>
        <div style={s.topActions}>
          <Button variant="secondary" icon={Landmark} onClick={() => navigate("/wallet/bank-details")}>{t("wallet.bankDetails")}</Button>
          <Button icon={History} onClick={() => navigate("/wallet/cashout-history")}>{t("wallet.cashoutHistory")}</Button>
        </div>
      </div>

      <div style={s.statsRow}>
        <Card style={s.statBox}>
          <div style={s.statValue}>{summary.totalEarning} €</div>
          <div style={s.statLabel}>{t("wallet.totalEarnings")}</div>
        </Card>
        <Card style={s.statBox}>
          <div style={{ ...s.statValue, color: color.success500 }}>{summary.totalReceived} €</div>
          <div style={s.statLabel}>{t("wallet.totalReceived")}</div>
        </Card>
        <Card style={s.statBox}>
          <div style={{ ...s.statValue, color: color.warning500 }}>{summary.totalReceivable} €</div>
          <div style={s.statLabel}>{t("wallet.pending")}</div>
        </Card>
      </div>

      {galleries.length === 0 ? (
        <EmptyState icon={WalletIcon} text={t("wallet.emptyState")} />
      ) : (
        <div style={s.list}>
          {galleries.map((g, i) => {
            const id = galleryId(g);
            return (
              <Card key={id ?? i} style={s.card}>
                <div style={s.cardTitle}>{g.EventName}</div>
                <div style={s.cardDivider} />
                <div style={s.row}><span style={s.label}>{t("wallet.galleryLabel")}</span><span style={s.value}>{g.GalleryName}</span></div>
                <div style={s.row}><span style={s.label}>{t("wallet.totalLabel")}</span><span style={s.value}>{g.Total} €</span></div>
                <div style={s.row}><span style={s.label}>{t("wallet.buyersLabel")}</span><span style={s.value}>{g.BuyerCount}</span></div>
                {id && (
                  <button type="button" className="btn btn-ghost btn-sm" style={s.detailBtn} onClick={() => navigate(`/wallet/gallery/${id}`)}>
                    {t("wallet.viewDetails")} <ChevronRight size={13} strokeWidth={2.5} />
                  </button>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {galleries.length > 0 && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={meta?.TotalCount ?? meta?.totalCount ?? 0}
          onPageChange={(p) => load(p)}
          disabled={loadingMore}
        />
      )}
    </div>
  );
};

const s = {
  backLink: { display: "inline-flex", marginBottom: 12 },
  topBar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 },
  title: { fontSize: 20, fontWeight: 800, color: color.textPrimary, margin: 0, letterSpacing: -0.3 },
  topActions: { display: "flex", gap: 10 },
  statsRow: { display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" },
  statBox: { flex: "1 1 160px", padding: "16px 18px" },
  statValue: { fontSize: 22, fontWeight: 800, color: color.primary600, letterSpacing: -0.4 },
  statLabel: { fontSize: 12, color: color.textMuted, marginTop: 4 },
  list: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 },
  card: { padding: 18, boxShadow: shadow.sm },
  cardTitle: { fontWeight: 700, fontSize: 14, color: color.primary600, marginBottom: 10 },
  cardDivider: { height: 1, background: color.gray100, marginBottom: 12 },
  row: { display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 13 },
  label: { color: color.textMuted },
  value: { fontWeight: 600, color: color.gray800 },
  detailBtn: { color: color.primary600, padding: "6px 0", marginTop: 4 },
};

export default Wallet;
